
import { useEffect, useRef, useState } from "react";

const SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;

function loadTurnstileScript() {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    let script = document.querySelector(
      "script[data-cloudflare-turnstile]"
    );

    const handleLoad = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        scriptPromise = null;
        reject(new Error("Turnstile failed to initialize."));
      }
    };

    const handleError = () => {
      scriptPromise = null;
      reject(new Error("Could not load Turnstile."));
    };

    if (!script) {
      script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.dataset.cloudflareTurnstile = "true";
    }

    script.addEventListener("load", handleLoad, {
      once: true,
    });

    script.addEventListener("error", handleError, {
      once: true,
    });

    if (!script.isConnected) {
      document.head.appendChild(script);
    }
  });

  return scriptPromise;
}

const TurnstileWidget = ({ onVerify }) => {
  const containerRef = useRef(null);
  const [widgetError, setWidgetError] = useState("");

  const siteKey =
    process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    let cancelled = false;
    let widgetId;

    setWidgetError("");

    if (!siteKey) {
      setWidgetError(
        "Security verification is not configured."
      );
      onVerify("");
      return undefined;
    }

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) {
          return;
        }

        widgetId = turnstile.render(
          containerRef.current,
          {
            sitekey: siteKey,
            theme: "auto",

            callback: (token) => {
              onVerify(token);
            },

            "expired-callback": () => {
              onVerify("");
            },

            "error-callback": () => {
              onVerify("");
              setWidgetError(
                "Verification failed to load. Please try again."
              );
            },
          }
        );
      })
      .catch((error) => {
        console.error("Turnstile error:", error);

        if (!cancelled) {
          setWidgetError(
            "Unable to load security verification. Please refresh the page."
          );
        }
      });

    return () => {
      cancelled = true;

      if (
        widgetId !== undefined &&
        window.turnstile
      ) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [siteKey, onVerify]);

  return (
    <div className="space-y-2">
      <div ref={containerRef} />

      {widgetError && (
        <p
          role="alert"
          className="text-sm text-red-600"
        >
          {widgetError}
        </p>
      )}
    </div>
  );
};

export default TurnstileWidget;
